import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { SPACING } from "../../common/constants/spacing";
import { useProject } from "../../common/contexts/ProjectContext";

const SalesTable = ({ sales, isOwner }) => (
  <TableContainer sx={{ overflowX: "auto" }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Type
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Unit price
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Unit price currency
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Quantity
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Is refund
            </Typography>
          </TableCell>
          {isOwner && (
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                Royalty amount
              </Typography>
            </TableCell>
          )}
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Royalty currency
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Period start
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              Period end
            </Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sales.map((sale, index) => (
          <TableRow key={index} hover>
            <TableCell>
              <Typography variant="body2">{sale.type}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{sale.unit_price}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {sale.unit_price_currency}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{sale.quantity}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{String(sale.is_refund)}</Typography>
            </TableCell>
            {isOwner && (
              <TableCell>
                <Typography variant="body2">{sale.royalty_amount}</Typography>
              </TableCell>
            )}
            <TableCell>
              <Typography variant="body2">{sale.royalty_currency}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {new Date(sale.period_start)?.toLocaleString()}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {new Date(sale.period_end)?.toLocaleString()}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const SalesDetails = ({ sales }) => {
  const { currentUserRole } = useProject();
  const isOwner = currentUserRole === "owner";

  if (!sales || sales.length === 0) {
    return null;
  }

  // Check if any sale has a source_label
  const hasLabels = sales.some((s) => s.source_label);

  if (!hasLabels) {
    // Original single-table layout when no labels are present
    return (
      <>
        <Typography
          variant="h2"
          fontWeight="bold"
          sx={{ mt: 4, mb: SPACING.sm }}
        >
          Sales Details
        </Typography>
        <Card sx={{ p: 0 }} variant="outlined">
          <SalesTable sales={sales} isOwner={isOwner} />
        </Card>
      </>
    );
  }

  // Group by source_label — null/empty goes into "Other"
  const groups = {};
  for (const sale of sales) {
    const key = sale.source_label || "__other__";
    if (!groups[key]) groups[key] = [];
    groups[key].push(sale);
  }

  // Sort: labeled groups first (alphabetically), then "Other"
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    if (a === "__other__") return 1;
    if (b === "__other__") return -1;
    return a.localeCompare(b);
  });

  return (
    <>
      <Typography variant="h2" fontWeight="bold" sx={{ mt: 4, mb: SPACING.sm }}>
        Sales Details
      </Typography>
      {sortedKeys.map((key) => (
        <Box key={key} sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Chip
              label={key === "__other__" ? "Other" : key}
              color={key === "__other__" ? "default" : "primary"}
              variant="outlined"
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {groups[key].length} record{groups[key].length !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <Card sx={{ p: 0 }} variant="outlined">
            <SalesTable sales={groups[key]} isOwner={isOwner} />
          </Card>
        </Box>
      ))}
    </>
  );
};

export default SalesDetails;
